import React, { useState, useEffect, useRef } from "react";
import {
    FiMenu,
    FiSearch,
    FiBell,
    FiChevronRight,
    FiUser,
    FiLogOut,
    FiSettings,
    FiClock,
} from "react-icons/fi";
import ThemeToggle from "../ThemeToggle";
import { ALL_MENU_ITEMS, MENU_BY_ID } from "./menuConfig";

/**
 * Sticky top bar: sidebar toggle, breadcrumb + page title, global search
 * (jumps to a menu), notifications, theme toggle and profile dropdown.
 *
 * The search + notification bell are navigational affordances only —
 * no data logic is duplicated here.
 */
export default function Topbar({ activeTab, onNavigate, onOpenSidebar, user, onLogout, notifications = [] }) {
    const [query, setQuery] = useState("");
    const [searchOpen, setSearchOpen] = useState(false);
    const [notifOpen, setNotifOpen] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);

    const searchRef = useRef(null);
    const notifRef = useRef(null);
    const profileRef = useRef(null);

    const current = MENU_BY_ID[activeTab];

    useEffect(() => {
        const onDoc = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false);
            if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
            if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const results = query.trim()
        ? ALL_MENU_ITEMS.filter((m) => m.label.toLowerCase().includes(query.trim().toLowerCase()))
        : ALL_MENU_ITEMS;

    const handleSelect = (id) => {
        onNavigate(id);
        setQuery("");
        setSearchOpen(false);
    };

    const initial = (user?.email || "A").charAt(0).toUpperCase();
    const unread = notifications.filter((n) => n.unread).length;

    return (
        <header className="sticky top-0 z-30 border-b border-slate-200/80 bg-white/80 backdrop-blur-md dark:border-slate-800 dark:bg-slate-900/80">
            <div className="flex h-16 items-center gap-3 px-4 sm:px-6">
                {/* Sidebar toggle (mobile) */}
                <button
                    onClick={onOpenSidebar}
                    className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 lg:hidden"
                    aria-label="Buka menu"
                >
                    <FiMenu className="h-5 w-5" />
                </button>

                {/* Breadcrumb + title */}
                <div className="min-w-0 flex-1">
                    <nav className="hidden items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500 sm:flex" aria-label="Breadcrumb">
                        <span>XF Admin</span>
                        {current?.group && (
                            <>
                                <FiChevronRight className="h-3 w-3" />
                                <span>{current.group}</span>
                            </>
                        )}
                        <FiChevronRight className="h-3 w-3" />
                        <span className="font-medium text-slate-600 dark:text-slate-300">{current?.label || "Dashboard"}</span>
                    </nav>
                    <h2 className="truncate text-base font-semibold text-slate-900 dark:text-white">
                        {current?.label || "Dashboard"}
                    </h2>
                </div>

                {/* Global search */}
                <div className="relative hidden md:block" ref={searchRef}>
                    <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                        value={query}
                        onFocus={() => setSearchOpen(true)}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setSearchOpen(true);
                        }}
                        placeholder="Cari menu..."
                        aria-label="Cari menu"
                        className="h-10 w-48 rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm text-slate-700 placeholder:text-slate-400 outline-none transition-all focus:w-64 focus:border-primary-500 focus:bg-white focus:ring-4 focus:ring-primary-500/10 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200 dark:focus:bg-slate-800 lg:w-56"
                    />
                    {searchOpen && (
                        <div className="absolute right-0 mt-2 w-64 overflow-hidden rounded-xl border border-slate-200/80 bg-white p-1.5 shadow-pop animate-fade-in-up dark:border-slate-800 dark:bg-slate-900">
                            {results.length === 0 ? (
                                <p className="px-3 py-3 text-sm text-slate-500 dark:text-slate-400">Menu tidak ditemukan</p>
                            ) : (
                                results.map((item) => {
                                    const Icon = item.icon;
                                    return (
                                        <button
                                            key={item.id}
                                            onClick={() => handleSelect(item.id)}
                                            className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                                        >
                                            <Icon className="h-4 w-4 text-slate-400" />
                                            {item.label}
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    )}
                </div>

                {/* Right cluster */}
                <div className="flex items-center gap-1.5">
                    <ThemeToggle />

                    {/* Notifications */}
                    <div className="relative" ref={notifRef}>
                        <button
                            onClick={() => setNotifOpen((v) => !v)}
                            className="relative rounded-lg p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                            aria-label="Notifikasi"
                        >
                            <FiBell className="h-5 w-5" />
                            {unread > 0 && (
                                <span className="absolute right-1.5 top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-danger-500 px-1 text-[10px] font-bold text-white">
                                    {unread > 9 ? "9+" : unread}
                                </span>
                            )}
                        </button>
                        {notifOpen && (
                            <div className="absolute right-0 mt-2 w-80 overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-pop animate-fade-in-up dark:border-slate-800 dark:bg-slate-900">
                                <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-slate-800">
                                    <p className="text-sm font-semibold text-slate-900 dark:text-white">Notifikasi</p>
                                    {unread > 0 && <span className="text-xs text-slate-400">{unread} baru</span>}
                                </div>
                                <div className="max-h-80 overflow-y-auto scrollbar-slim">
                                    {notifications.length === 0 ? (
                                        <p className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                                            Belum ada notifikasi
                                        </p>
                                    ) : (
                                        notifications.map((n, i) => (
                                            <button
                                                key={i}
                                                onClick={() => {
                                                    if (n.tab) handleSelect(n.tab);
                                                    setNotifOpen(false);
                                                }}
                                                className="flex w-full gap-3 border-b border-slate-50 px-4 py-3 text-left transition-colors last:border-0 hover:bg-slate-50 dark:border-slate-800/60 dark:hover:bg-slate-800/50"
                                            >
                                                <div className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${n.toneClass || "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"}`}>
                                                    {n.icon ? <n.icon className="h-4 w-4" /> : <FiClock className="h-4 w-4" />}
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-200">{n.title}</p>
                                                    {n.description && (
                                                        <p className="truncate text-xs text-slate-500 dark:text-slate-400">{n.description}</p>
                                                    )}
                                                </div>
                                                {n.unread && <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-primary-500" />}
                                            </button>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Profile */}
                    <div className="relative" ref={profileRef}>
                        <button
                            onClick={() => setProfileOpen((v) => !v)}
                            className="flex items-center gap-2.5 rounded-xl py-1 pl-1 pr-2 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800"
                            aria-label="Menu profil"
                        >
                            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 text-sm font-semibold text-white">
                                {initial}
                            </span>
                            <span className="hidden text-left lg:block">
                                <span className="block max-w-[140px] truncate text-sm font-medium text-slate-800 dark:text-slate-200">
                                    {user?.email || "Admin"}
                                </span>
                                <span className="block text-[11px] text-slate-400 dark:text-slate-500">Administrator</span>
                            </span>
                        </button>
                        {profileOpen && (
                            <div className="absolute right-0 mt-2 w-64 overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-pop animate-fade-in-up dark:border-slate-800 dark:bg-slate-900">
                                <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-3.5 dark:border-slate-800">
                                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 text-sm font-semibold text-white">
                                        {initial}
                                    </span>
                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                                            {user?.email || "Admin"}
                                        </p>
                                        <p className="text-xs text-slate-400 dark:text-slate-500">Administrator</p>
                                    </div>
                                </div>
                                <div className="p-1.5">
                                    <button
                                        onClick={() => {
                                            handleSelect("settings");
                                            setProfileOpen(false);
                                        }}
                                        className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                                    >
                                        <FiSettings className="h-4 w-4" />
                                        Pengaturan
                                    </button>
                                    <button
                                        onClick={() => {
                                            handleSelect("dashboard");
                                            setProfileOpen(false);
                                        }}
                                        className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                                    >
                                        <FiUser className="h-4 w-4" />
                                        Profil
                                    </button>
                                    <div className="my-1.5 h-px bg-slate-100 dark:bg-slate-800" />
                                    <button
                                        onClick={onLogout}
                                        className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium text-danger-600 transition-colors hover:bg-danger-50 dark:text-danger-400 dark:hover:bg-danger-500/10"
                                    >
                                        <FiLogOut className="h-4 w-4" />
                                        Logout
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
}
